// src/components/functions/listeners.ts
import { Salon } from '../../../../../types'; // Adjust path as needed
import { updateMesa } from '../../../../../../../../crud/management_api/mesas';
import { updateObjeto } from '../../../../../../../../crud/management_api/objectos';
import { toast } from 'react-toastify';

// Helper clamp function
type Numeric = number;
const clamp = (value: Numeric, min: Numeric, max: Numeric): Numeric =>
  Math.max(min, Math.min(value, max));

const parseCssNumber = (value: string, fallback: number): number => {
  const parsed = Number.parseFloat((value || '').trim());
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getPositionUnitPx = (
  gridElement: HTMLDivElement,
  scaleFactor: number,
  isMesa: boolean
): number => {
  const gridStyles = window.getComputedStyle(gridElement);

  if (isMesa) {
    const remPx = parseCssNumber(window.getComputedStyle(document.documentElement).fontSize, 16);
    const compensation = parseCssNumber(
      gridStyles.getPropertyValue('--salon-scale-compensation'),
      1.185
    );
    return remPx * scaleFactor * compensation;
  }

  const emPx = parseCssNumber(gridStyles.fontSize, 16);
  const compensation = parseCssNumber(
    gridStyles.getPropertyValue('--salon-objects-compensation'),
    1.1
  );
  return emPx * scaleFactor * compensation;
};

// Helper function to check if more than 50% of item is outside grid bounds
export const isMoreThanHalfOutOfBounds = (
  itemX: number,
  itemY: number,
  itemWidth: number,
  itemHeight: number,
  gridWidth: number,
  gridHeight: number
): boolean => {
  // Calculate item boundaries
  const itemLeft = itemX;
  const itemRight = itemX + itemWidth;
  const itemTop = itemY;
  const itemBottom = itemY + itemHeight;

  // Calculate overlap with grid
  const overlapLeft = Math.max(0, Math.min(itemRight, gridWidth) - Math.max(itemLeft, 0));
  const overlapTop = Math.max(0, Math.min(itemBottom, gridHeight) - Math.max(itemTop, 0));
  const overlapArea = overlapLeft * overlapTop;

  // Calculate total item area
  const itemArea = itemWidth * itemHeight;

  // Check if less than 50% is inside (more than 50% outside)
  return overlapArea < (itemArea * 0.5);
};

// Helper function to find maximum allowed position where exactly 50% is outside
export const getMaxAllowedPosition = (
  currentX: number,
  currentY: number,
  deltaX: number,
  deltaY: number,
  itemWidth: number,
  itemHeight: number,
  gridWidth: number,
  gridHeight: number
): { x: number; y: number } => {
  let newX = currentX;
  let newY = currentY;

  // For X movement (horizontal)
  if (deltaX !== 0) {
    if (deltaX > 0) {
      // Moving right - allow up to 50% outside right edge
      newX = Math.min(currentX + deltaX, gridWidth - (itemWidth * 0.5));
    } else {
      // Moving left - allow up to 50% outside left edge
      newX = Math.max(currentX + deltaX, -(itemWidth * 0.5));
    }
  }

  // For Y movement (vertical)
  if (deltaY !== 0) {
    if (deltaY > 0) {
      // Moving down - allow up to 50% outside bottom edge
      newY = Math.min(currentY + deltaY, gridHeight - (itemHeight * 0.5));
    } else {
      // Moving up - allow up to 50% outside top edge
      newY = Math.max(currentY + deltaY, -(itemHeight * 0.5));
    }
  }

  return { x: newX, y: newY };
};

/**
 * Check if an item is more than 50% outside the grid bounds
 */
export const isItemTooFarOutOfBounds = (
  itemX: number,
  itemY: number,
  itemWidth: number,
  itemHeight: number,
  gridWidth: number,
  gridHeight: number
): boolean => {
  // Calculate item bounds
  const itemLeft = itemX;
  const itemRight = itemX + itemWidth;
  const itemTop = itemY;
  const itemBottom = itemY + itemHeight;

  // Calculate how much of the item is outside each edge
  const leftOverflow = Math.max(0, -itemLeft);
  const rightOverflow = Math.max(0, itemRight - gridWidth);
  const topOverflow = Math.max(0, -itemTop);
  const bottomOverflow = Math.max(0, itemBottom - gridHeight);

  // Check if any dimension has more than 50% overflow
  const leftPercentage = leftOverflow / itemWidth;
  const rightPercentage = rightOverflow / itemWidth;
  const topPercentage = topOverflow / itemHeight;
  const bottomPercentage = bottomOverflow / itemHeight;

  return leftPercentage > 0.5 || rightPercentage > 0.5 || topPercentage > 0.5 || bottomPercentage > 0.5;
};

/**
 * Get the boundaries violation info for visual feedback
 */
export const getBoundaryViolationInfo = (
  itemX: number,
  itemY: number,
  itemWidth: number,
  itemHeight: number,
  gridWidth: number,
  gridHeight: number
) => {
  const itemLeft = itemX;
  const itemRight = itemX + itemWidth;
  const itemTop = itemY;
  const itemBottom = itemY + itemHeight;

  const leftOverflow = Math.max(0, -itemLeft);
  const rightOverflow = Math.max(0, itemRight - gridWidth);
  const topOverflow = Math.max(0, -itemTop);
  const bottomOverflow = Math.max(0, itemBottom - gridHeight);

  const leftPercentage = leftOverflow / itemWidth;
  const rightPercentage = rightOverflow / itemWidth;
  const topPercentage = topOverflow / itemHeight;
  const bottomPercentage = bottomOverflow / itemHeight;

  return {
    hasViolation: leftPercentage > 0.5 || rightPercentage > 0.5 || topPercentage > 0.5 || bottomPercentage > 0.5,
    violations: {
      left: leftPercentage > 0.5,
      right: rightPercentage > 0.5,
      top: topPercentage > 0.5,
      bottom: bottomPercentage > 0.5,
    },
    percentages: {
      left: leftPercentage,
      right: rightPercentage,
      top: topPercentage,
      bottom: bottomPercentage,
    }
  };
};

/**
 * Handle keyboard movement, clamping to grid bounds using real pixel dimensions.
 * Now includes 50% out-of-bounds prevention logic.
 */
export const handleKeyPress = (
  event: KeyboardEvent,
  selectedItem: any,
  selectedSalon: Salon | undefined,
  updateSelectedItem: Function,
  updateSalon: Function,
  salones?: Salon[],
  setSalones?: (salones: Salon[]) => void,
  gridElement?: HTMLDivElement | null,
  gridDimsPx?: { maxWidth: number; maxHeight: number },
  itemDimsPx?: { width: number; height: number } | null,
  cellWidthPx?: number,
  cellHeightPx?: number,
  scaleFactor: number = 1,
  setShowBoundaryWarning?: Function,
  setGridViolation?: Function
) => {
  
  if (!selectedItem || !selectedSalon || cellWidthPx == null || cellHeightPx == null) {
    return;
  }
  // Determine direction
  let deltaX = 0;
  let deltaY = 0;
  switch (event.key) {
    case 'ArrowUp':    deltaY = -1; break;
    case 'ArrowDown':  deltaY = 1;  break;
    case 'ArrowLeft':  deltaX = -1; break;
    case 'ArrowRight': deltaX = 1;  break;
    default: return;
  }
  event.preventDefault();

  // Get grid bounds first
  let gridWidth = Infinity;
  let gridHeight = Infinity;
  if (gridDimsPx) {
    gridWidth = gridDimsPx.maxWidth;
    gridHeight = gridDimsPx.maxHeight;
  } else if (gridElement) {
    const rect = gridElement.getBoundingClientRect();
    gridWidth = rect.width;
    gridHeight = rect.height;
  }

  if (!gridElement) {
    return;
  }

  const isMesa = 'num' in selectedItem;
  const positionUnitPx = getPositionUnitPx(gridElement, scaleFactor, isMesa);

  // Raw pixel positions in the same unit space used by the rendered left/top values.
  const rawXpx = selectedItem.x_pos * positionUnitPx + deltaX * positionUnitPx;
  const rawYpx = selectedItem.y_pos * positionUnitPx + deltaY * positionUnitPx;

  // Keep dimensions and movement in the same pixel space.
  const itemWidthPx = selectedItem.width * scaleFactor;
  const itemHeightPx = selectedItem.height * scaleFactor;
  
  // COMMENTED OUT: 50% boundary checking disabled - needs fixing
  // const minXpx = -(itemWidthPx * 0.5); // 50% of item can be outside left edge
  // const maxXpx = gridWidth - (itemWidthPx * 0.5); // 50% of item can be outside right edge
  // const minYpx = -(itemHeightPx * 0.5); // 50% of item can be outside top edge
  // const maxYpx = gridHeight - (itemHeightPx * 0.5); // 50% of item can be outside bottom edge

  // const gridCellsX = 30; // Should match SalonDisplayer value  
  // const gridCellsY = 30; // Should match SalonDisplayer value
  // const pixelsPerGridCellX = gridWidth / gridCellsX;
  // const pixelsPerGridCellY = gridHeight / gridCellsY;
  // 
  // const validationXpx = (selectedItem.x_pos + deltaX) * pixelsPerGridCellX;
  // const validationYpx = (selectedItem.y_pos + deltaY) * pixelsPerGridCellY;
  // 
  // const currentXpx = selectedItem.x_pos * pixelsPerGridCellX;
  // const currentYpx = selectedItem.y_pos * pixelsPerGridCellY;
  // 
  // const currentViolation = isMoreThanHalfOutOfBounds(currentXpx, currentYpx, itemWidthPx, itemHeightPx, gridWidth, gridHeight);
  // const newViolation = isMoreThanHalfOutOfBounds(validationXpx, validationYpx, itemWidthPx, itemHeightPx, gridWidth, gridHeight);
  // 
  // const currentViolationInfo = getBoundaryViolationInfo(currentXpx, currentYpx, itemWidthPx, itemHeightPx, gridWidth, gridHeight);
  // const newViolationInfo = getBoundaryViolationInfo(validationXpx, validationYpx, itemWidthPx, itemHeightPx, gridWidth, gridHeight);
  // 
  // let isRecoveryMovement = false;
  // 
  // if (currentViolation) {
  //   if (currentViolationInfo.violations.left && deltaX > 0) {
  //     isRecoveryMovement = true;
  //   }
  //   if (currentViolationInfo.violations.right && deltaX < 0) {
  //     isRecoveryMovement = true;
  //   }
  //   if (currentViolationInfo.violations.top && deltaY > 0) {
  //     isRecoveryMovement = true;
  //   }
  //   if (currentViolationInfo.violations.bottom && deltaY < 0) {
  //     isRecoveryMovement = true;
  //   }
  // }
  // 
  // const shouldBlockMovement = (!currentViolation && newViolation) || 
  //                             (currentViolation && !isRecoveryMovement && newViolation);
  // 
  // if (shouldBlockMovement) {
  //   if (setShowBoundaryWarning) {
  //     setShowBoundaryWarning(true);
  //     setTimeout(() => setShowBoundaryWarning(false), 3000);
  //   } 
  //   
  //   if (setGridViolation) {
  //     setGridViolation(true);
  //     setTimeout(() => setGridViolation(false), 2000);
  //   } 
  //   
  //   return; // Exit early - prevent the movement
  // } else if (isRecoveryMovement) {
  //   if (setGridViolation) {
  //     setGridViolation(false);
  //   }
  // } else {
  //   if (setGridViolation) {
  //     setGridViolation(false);
  //   }
  // }

  // Hard boundaries: the item must remain fully inside the salon container.
  const maxXpx = Math.max(0, gridWidth - itemWidthPx);
  const maxYpx = Math.max(0, gridHeight - itemHeightPx);
  const clampedXpx = clamp(rawXpx, 0, maxXpx);
  const clampedYpx = clamp(rawYpx, 0, maxYpx);

  const maxXUnits = Math.max(0, Math.floor(maxXpx / positionUnitPx));
  const maxYUnits = Math.max(0, Math.floor(maxYpx / positionUnitPx));
  const newX = clamp(Math.round(clampedXpx / positionUnitPx), 0, maxXUnits);
  const newY = clamp(Math.round(clampedYpx / positionUnitPx), 0, maxYUnits);

  // Build updated salon
  let updatedSalon: Salon;
  if ('num' in selectedItem) {
    updatedSalon = {
      ...selectedSalon,
      mesas: selectedSalon.mesas.map(mesa =>
        mesa.id === selectedItem.id ? { ...mesa, x_pos: newX, y_pos: newY } : mesa
      )
    };
  } else {
    updatedSalon = {
      ...selectedSalon,
      objetos: selectedSalon.objetos.map(objeto =>
        objeto.id === selectedItem.id ? { ...objeto, x_pos: newX, y_pos: newY } : objeto
      )
    };
  }

  // Update global state if provided
  if (salones && setSalones) {
    setSalones(
      salones.map(salon =>
        salon.salon_id === updatedSalon.salon_id ? updatedSalon : salon
      )
    );
  }

  // Apply updates
  updateSalon(updatedSalon);
  updateSelectedItem({ ...selectedItem, x_pos: newX, y_pos: newY });
};

/**
 * Adjust scaleFactor based on window size.
 */
export const handleResize = (setScaleFactor: any) => {
  const baseWidth = 900;
  const baseHeight = 950;
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;
  const scaleWidth = windowWidth / baseWidth;
  const scaleHeight = windowHeight / baseHeight;
  const newScaleFactor = Math.min(scaleWidth, scaleHeight);
  setScaleFactor(newScaleFactor);
};

/**
 * Begin dragging an item.
 */
export const handleDragStart = (
  event: React.DragEvent<HTMLDivElement>,
  item: any
) => {
  const dragData = JSON.stringify({
    id:   item?.id,
    type: 'num' in item ? 'Mesa' : 'Objeto'
  });
  event.dataTransfer.setData('application/reactflow', dragData);
  event.dataTransfer.effectAllowed = 'move';
};

/**
 * Handle drop, clamping within grid bounds with 50% out-of-bounds validation.
 */
export const handleDrop = async (
  event: React.DragEvent<HTMLDivElement>,
  selectedSalon: Salon | undefined,
  updateSalon: (salon: Salon) => void,
  scaleFactor: number,
  showBoundaryWarning?: (message: string) => void
) => {
  event.preventDefault();
  const { id, type } = JSON.parse(
    event.dataTransfer.getData('application/reactflow')
  );

  if (!selectedSalon) return;

  // Find the item being dragged
  let draggedItem: any = null;
  if (type === 'Mesa') {
    draggedItem = selectedSalon.mesas.find(mesa => mesa.id === id);
  } else {
    draggedItem = selectedSalon.objetos.find(objeto => objeto.id === id);
  }

  if (!draggedItem) return;

  // Measure grid bounds in pixels
  const gridRect = event.currentTarget.getBoundingClientRect();
  // Calculate raw coords in pixels
  const rawXpx = event.clientX - gridRect.left;
  const rawYpx = event.clientY - gridRect.top;

  const gridElement = event.currentTarget as HTMLDivElement;
  const isMesa = type === 'Mesa';
  const positionUnitPx = getPositionUnitPx(gridElement, scaleFactor, isMesa);

  const itemWidthPx = draggedItem.width * scaleFactor;
  const itemHeightPx = draggedItem.height * scaleFactor;
  const maxXpx = Math.max(0, gridRect.width - itemWidthPx);
  const maxYpx = Math.max(0, gridRect.height - itemHeightPx);
  const clampedXpx = clamp(rawXpx, 0, maxXpx);
  const clampedYpx = clamp(rawYpx, 0, maxYpx);

  const maxXUnits = Math.max(0, Math.floor(maxXpx / positionUnitPx));
  const maxYUnits = Math.max(0, Math.floor(maxYpx / positionUnitPx));
  const newX = clamp(Math.round(clampedXpx / positionUnitPx), 0, maxXUnits);
  const newY = clamp(Math.round(clampedYpx / positionUnitPx), 0, maxYUnits);

  if (type === 'Mesa') {
    const updatedMesas = selectedSalon.mesas.map(mesa =>
      mesa.id === id ? { ...mesa, x_pos: newX, y_pos: newY } : mesa
    );
    updateSalon({ ...selectedSalon, mesas: updatedMesas });
    try {
      await updateMesa(id, { x_position: newX, y_position: newY });
      toast.success('Posición de la mesa actualizada');
    } catch (error) {
      console.error('Error al actualizar la posición de la mesa:', error);
      toast.error('Error al guardar la posición');
    }
  } else {
    const updatedObjetos = selectedSalon.objetos.map(objeto =>
      objeto.id === id ? { ...objeto, x_pos: newX, y_pos: newY } : objeto
    );
    updateSalon({ ...selectedSalon, objetos: updatedObjetos });
    try {
      await updateObjeto(id, { x_position: newX, y_position: newY });
      toast.success('Posición del objeto actualizada');
    } catch (error) {
      console.error('Error al actualizar la posición del objeto:', error);
      toast.error('Error al guardar la posición');
    }
  }
};

/**
 * Allow dropping by preventing default.
 */
export const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
  event.preventDefault();
};
